"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { AUTONOMY_PRACTITIONER_NPIS, buildTeReasonContext, generateTeMessage, generateTeReasonText, getMissingInformationSummary, hasAutonomyDiagnosisRules, hasSleepDiagnosis, isOptumServeReferral, requiresHmoReferral } from "@/configuration/groups/autonomy/configuration";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { PDFViewer } from "@/src/components/pdf-viewer/pdf-viewer";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/src/components/shadcn/ui/dialog";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { formatDateForFilename } from "@/src/lib/utils/date-and-phone-utils";
import { isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskSource } from "@/src/models/task";
const AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID: Record<string, string> = Object.fromEntries(Object.entries(AUTONOMY_PRACTITIONER_NPIS).map(([npi, info]) => [info.ehrId, npi]));
interface AutonomyReferralInstructionsProps {
  referral: Referral;
  task: Task;
  faxSenderNumber?: string | null;
}
const AUTONOMY_ECW_URL = "https://gacent96bbda5pqn16app.ecwcloud.com/mobiledoc/jsp/webemr/index.jsp";
function findOutboundFaxFile(task: Task) {
  return task.files.find(file => file.source === TaskSource.FAX_OUTBOUND);
}
function getProviderName(provider: Practitioner | null, fallback: string): string {
  if (provider) return getPractitionerNameWithCredentials(provider) || fallback;else return fallback;
}
function AssignmentNote() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="text-xs text-muted-foreground italic">Note: Verify assignment based on patient's last encounter provider</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function AutonomyReferralMessage(t0) {
  const $ = _c(117);
  const {
    referral,
    task,
    faxSenderNumber: t1
  } = t0;
  const faxSenderNumber = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== referral || $[1] !== task) {
    const teContext = buildTeReasonContext(referral, task, null);
    t2 = generateTeReasonText(teContext);
    $[0] = referral;
    $[1] = task;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const teReason = t2;
  let t3;
  if ($[3] !== faxSenderNumber || $[4] !== referral || $[5] !== task) {
    t3 = generateTeMessage(referral, task, faxSenderNumber);
    $[3] = faxSenderNumber;
    $[4] = referral;
    $[5] = task;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const teMessage = t3;
  const missingInfo = getMissingInformationSummary(referral, task);
  let t4;
  if ($[7] !== referral) {
    t4 = requiresHmoReferral(referral);
    $[7] = referral;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const needsHmoReferral = t4;
  let t5;
  if ($[9] !== referral.schedulingPriority) {
    t5 = isStatOrUrgentPriority(referral.schedulingPriority);
    $[9] = referral.schedulingPriority;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isUrgent = t5;
  let t6;
  if ($[11] !== referral) {
    t6 = hasSleepDiagnosis(referral);
    $[11] = referral;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const isSleepDiagnosis = t6;
  let t7;
  if ($[13] !== faxSenderNumber || $[14] !== referral) {
    t7 = isOptumServeReferral(faxSenderNumber, referral);
    $[13] = faxSenderNumber;
    $[14] = referral;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const isOptumServe = t7;
  const {
    data: matchedProviders
  } = useProvidersForDiagnostics(referral.diagnosisList ?? null);
  const outboundFaxFile = findOutboundFaxFile(task);
  const [dialogOpen, setDialogOpen] = useState(false);
  let t8;
  if ($[16] !== referral.dateReceived) {
    t8 = formatDateForFilename(referral.dateReceived, "");
    $[16] = referral.dateReceived;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== referral.referringProvider) {
    t9 = getProviderName(referral.referringProvider, "Unknown Provider");
    $[18] = referral.referringProvider;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const fileName = `${t8} Referral from ${t9}`;
  let t10;
  if ($[20] !== referral.receivingProvider) {
    t10 = getProviderName(referral.receivingProvider, "Rendering Provider");
    $[20] = referral.receivingProvider;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const renderingProviderName = t10;
  const npi = referral.receivingProvider?.npiNumber ?? null;
  const supervisorLeadMa = (npi ? AUTONOMY_PRACTITIONER_MAPPINGS[npi] : null)?.supervisorLeadMa || null;
  const staffName = isSleepDiagnosis ? "Nicole Dunlap" : supervisorLeadMa;
  let t11;
  if ($[22] !== matchedProviders) {
    t11 = new Set((matchedProviders ?? []).map(_temp).filter(_temp2));
    $[22] = matchedProviders;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const matchedNpis = t11;
  let t12;
  if ($[24] !== isSleepDiagnosis || $[25] !== matchedNpis || $[26] !== matchedProviders?.length || $[27] !== npi) {
    t12 = !isSleepDiagnosis && hasAutonomyDiagnosisRules(npi) && !!matchedProviders?.length && !matchedNpis.has(npi);
    $[24] = isSleepDiagnosis;
    $[25] = matchedNpis;
    $[26] = matchedProviders?.length;
    $[27] = npi;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const hasProviderConflict = t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[29] !== fileName || $[30] !== hasProviderConflict || $[31] !== isSleepDiagnosis || $[32] !== isUrgent || $[33] !== matchedProviders || $[34] !== referral.schedulingPriority || $[35] !== renderingProviderName || $[36] !== staffName || $[37] !== teMessage || $[38] !== teReason) {
    const alternativeProviderNames = (matchedProviders ?? []).map(_temp3);
    t21 = "flex flex-col gap-1";
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>;
      $[49] = t22;
    } else {
      t22 = $[49];
    }
    t13 = "text-muted-foreground flex flex-col gap-2";
    let t23;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <span className="font-semibold">1. Create or Update the patient in eCW:{" "}<a href={AUTONOMY_ECW_URL} target="_blank" rel="noopener noreferrer" className="hover:bg-accent rounded p-1">Link</a></span>;
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <div className="flex flex-col gap-2">{t23}<div className="ml-4 space-y-1 text-xs"><div>Complete: Pharmacy, Additional Information, Responsible Party sections</div><div>Run insurance eligibility if plan type is unclear</div></div></div>;
      $[51] = t14;
    } else {
      t14 = $[51];
    }
    let t24;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <span className="font-semibold">2. Upload the file to the proper folder</span>;
      $[52] = t24;
    } else {
      t24 = $[52];
    }
    let t25;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <span className="font-medium">Document Type:</span>;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    let t26;
    let t27;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <div>{t25} <CopyableContent text="Patient Referral" /></div>;
      t27 = <span className="font-medium">ECW Folder:</span>;
      $[54] = t26;
      $[55] = t27;
    } else {
      t26 = $[54];
      t27 = $[55];
    }
    let t28;
    let t29;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <div>{t27} <CopyableContent text="Outside Records->Referrals" /></div>;
      t29 = <span className="font-medium">File name:</span>;
      $[56] = t28;
      $[57] = t29;
    } else {
      t28 = $[56];
      t29 = $[57];
    }
    let t30;
    if ($[58] !== fileName) {
      t30 = <div>{t29} <CopyableContent text={fileName} /></div>;
      $[58] = fileName;
      $[59] = t30;
    } else {
      t30 = $[59];
    }
    let t31;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <span className="font-medium">Assign to (receiving provider):</span>;
      $[60] = t31;
    } else {
      t31 = $[60];
    }
    const t32 = staffName ?? `${renderingProviderName} - Staff`;
    let t33;
    if ($[61] !== t32) {
      t33 = <div>{t31}{" "}<CopyableContent text={t32} /></div>;
      $[61] = t32;
      $[62] = t33;
    } else {
      t33 = $[62];
    }
    let t34;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <AssignmentNote />;
      $[63] = t34;
    } else {
      t34 = $[63];
    }
    let t35;
    if ($[64] !== t33) {
      t35 = <div className="space-y-1">{t33}{t34}</div>;
      $[64] = t33;
      $[65] = t35;
    } else {
      t35 = $[65];
    }
    let t36;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t36 = <div className="text-xs text-muted-foreground italic">BCBS referrals: leave document open. All other insurance: mark as reviewed.</div>;
      $[66] = t36;
    } else {
      t36 = $[66];
    }
    if ($[67] !== t30 || $[68] !== t35) {
      t15 = <div className="flex flex-col gap-2">{t24}<div className="ml-4 space-y-2">{t26}{t28}{t30}{t35}{t36}</div></div>;
      $[67] = t30;
      $[68] = t35;
      $[69] = t15;
    } else {
      t15 = $[69];
    }
    let t37;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = <span className="font-semibold">3. Telephone Encounter</span>;
      $[70] = t37;
    } else {
      t37 = $[70];
    }
    let t38;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t38 = <div className="font-medium">Assignment (receiving provider)</div>;
      $[71] = t38;
    } else {
      t38 = $[71];
    }
    const t39 = staffName ?? `${renderingProviderName} - Staff`;
    let t40;
    if ($[72] !== t39) {
      t40 = <div><CopyableContent text={t39} /></div>;
      $[72] = t39;
      $[73] = t40;
    } else {
      t40 = $[73];
    }
    let t41;
    if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
      t41 = <AssignmentNote />;
      $[74] = t41;
    } else {
      t41 = $[74];
    }
    let t42;
    if ($[75] !== t40) {
      t42 = <div>{t38}<div className="ml-2 text-sm space-y-1">{t40}{t41}</div></div>;
      $[75] = t40;
      $[76] = t42;
    } else {
      t42 = $[76];
    }
    let t43;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t43 = <div className="font-medium">Reason</div>;
      $[77] = t43;
    } else {
      t43 = $[77];
    }
    let t44;
    if ($[78] !== teReason) {
      t44 = <div className="ml-2 bg-muted/50 p-2 rounded"><CopyableContent text={teReason} /></div>;
      $[78] = teReason;
      $[79] = t44;
    } else {
      t44 = $[79];
    }
    let t45;
    if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
      t45 = <div className="ml-2 mt-1 text-xs text-muted-foreground italic">Add NP (New Patient) or EP (Established Patient) prefix based on encounter history</div>;
      $[80] = t45;
    } else {
      t45 = $[80];
    }
    let t46;
    if ($[81] !== t44) {
      t46 = <div>{t43}{t44}{t45}</div>;
      $[81] = t44;
      $[82] = t46;
    } else {
      t46 = $[82];
    }
    let t47;
    if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
      t47 = <div className="font-medium">Message</div>;
      $[83] = t47;
    } else {
      t47 = $[83];
    }
    let t48;
    if ($[84] !== teMessage) {
      t48 = <div>{t47}<div className="ml-2 bg-muted/50 p-3 rounded"><div className="text-sm whitespace-pre-wrap font-mono">{teMessage}</div></div></div>;
      $[84] = teMessage;
      $[85] = t48;
    } else {
      t48 = $[85];
    }
    if ($[86] !== t42 || $[87] !== t46 || $[88] !== t48) {
      t16 = <div className="flex flex-col gap-2">{t37}<div className="ml-4 space-y-3">{t42}{t46}{t48}</div></div>;
      $[86] = t42;
      $[87] = t46;
      $[88] = t48;
      $[89] = t16;
    } else {
      t16 = $[89];
    }
    if ($[90] !== isUrgent || $[91] !== referral.schedulingPriority) {
      t17 = isUrgent && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ Scheduling priority is {referral.schedulingPriority}. Mark the Telephone Encounter as High Priority in eCW.</AlertDescription></Alert>;
      $[90] = isUrgent;
      $[91] = referral.schedulingPriority;
      $[92] = t17;
    } else {
      t17 = $[92];
    }
    if ($[93] !== isSleepDiagnosis) {
      t18 = isSleepDiagnosis && <Alert className="mt-2"><AlertDescription className="text-sm">Sleep-related diagnosis — Use Eslin Luka as rendering provider.</AlertDescription></Alert>;
      $[93] = isSleepDiagnosis;
      $[94] = t18;
    } else {
      t18 = $[94];
    }
    if ($[95] !== isSleepDiagnosis) {
      t19 = isSleepDiagnosis && <Alert className="mt-2"><AlertDescription className="text-sm">Sleep-related diagnosis — Assign referral document and Telephone Encounter to Nicole Dunlap.</AlertDescription></Alert>;
      $[95] = isSleepDiagnosis;
      $[96] = t19;
    } else {
      t19 = $[96];
    }
    t20 = hasProviderConflict && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ {renderingProviderName} doesn't typically accept this diagnosis per the scheduling guide. Consider: {alternativeProviderNames.join(", ")}.</AlertDescription></Alert>;
    $[29] = fileName;
    $[30] = hasProviderConflict;
    $[31] = isSleepDiagnosis;
    $[32] = isUrgent;
    $[33] = matchedProviders;
    $[34] = referral.schedulingPriority;
    $[35] = renderingProviderName;
    $[36] = staffName;
    $[37] = teMessage;
    $[38] = teReason;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t13 = $[39];
    t14 = $[40];
    t15 = $[41];
    t16 = $[42];
    t17 = $[43];
    t18 = $[44];
    t19 = $[45];
    t20 = $[46];
    t21 = $[47];
    t22 = $[48];
  }
  let t23;
  if ($[97] !== needsHmoReferral) {
    t23 = needsHmoReferral && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ Patient Needs PCP/HMO Referral. Please contact referring provider.</AlertDescription></Alert>;
    $[97] = needsHmoReferral;
    $[98] = t23;
  } else {
    t23 = $[98];
  }
  let t24;
  if ($[99] !== isOptumServe) {
    t24 = isOptumServe && <Alert className="mt-2"><AlertDescription className="text-sm">Optum Serve referral; only Gincy Samuel, MD accepts these referrals</AlertDescription></Alert>;
    $[99] = isOptumServe;
    $[100] = t24;
  } else {
    t24 = $[100];
  }
  const t25 = missingInfo.length > 0 && <Alert className="mt-2"><AlertDescription className="text-sm"><div>ℹ️ Requested the following - please check if they've arrived before reaching out to the provider:</div><ul className="list-disc list-inside space-y-1 mt-2">{missingInfo.map(_temp4)}</ul>{outboundFaxFile && <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogTrigger asChild={true}><Button variant="outline" size="sm" className="mt-3">View Outbound Fax</Button></DialogTrigger><DialogContent className="max-w-4xl h-[80vh]"><DialogHeader><DialogTitle>Outbound Fax - Missing Information Request</DialogTitle></DialogHeader><div className="flex-1 overflow-hidden"><PDFViewer url={fileViewUrl(outboundFaxFile.pathToFile)} /></div></DialogContent></Dialog>}</AlertDescription></Alert>;
  let t26;
  if ($[101] !== t13 || $[102] !== t14 || $[103] !== t15 || $[104] !== t16 || $[105] !== t17 || $[106] !== t18 || $[107] !== t19 || $[108] !== t20 || $[109] !== t23 || $[110] !== t24 || $[111] !== t25) {
    t26 = <div className={t13}>{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t23}{t24}{t25}</div>;
    $[101] = t13;
    $[102] = t14;
    $[103] = t15;
    $[104] = t16;
    $[105] = t17;
    $[106] = t18;
    $[107] = t19;
    $[108] = t20;
    $[109] = t23;
    $[110] = t24;
    $[111] = t25;
    $[112] = t26;
  } else {
    t26 = $[112];
  }
  let t27;
  if ($[113] !== t21 || $[114] !== t22 || $[115] !== t26) {
    t27 = <div className={t21}>{t22}{t26}</div>;
    $[113] = t21;
    $[114] = t22;
    $[115] = t26;
    $[116] = t27;
  } else {
    t27 = $[116];
  }
  return t27;
}
function _temp4(item) {
  return <li key={item}>{item}</li>;
}
function _temp3(p_0) {
  return p_0.name;
}
function _temp2(matched) {
  return !!matched;
}
function _temp(p) {
  return AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID[p.ehrId];
}
function AutonomyReferralCards(t0) {
  const $ = _c(15);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.patient) {
    t1 = <PatientCard patient={referral.patient} />;
    $[0] = referral.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== referral) {
    t2 = <ReferralProviderCards referral={referral} />;
    t3 = <ReferralCard referral={referral} isEditable={true} />;
    $[2] = referral;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== referral.primaryCoverage) {
    t4 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[5] = referral.primaryCoverage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== referral.secondaryCoverage) {
    t5 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[7] = referral.secondaryCoverage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export function AutonomyReferralInstructions(t0) {
  const $ = _c(9);
  const {
    referral,
    task,
    faxSenderNumber: t1
  } = t0;
  const faxSenderNumber = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== faxSenderNumber || $[1] !== referral || $[2] !== task) {
    t2 = <EhrInstructionsCard><div className="text-sm text-muted-foreground"><AutonomyReferralMessage referral={referral} task={task} faxSenderNumber={faxSenderNumber} /></div></EhrInstructionsCard>;
    $[0] = faxSenderNumber;
    $[1] = referral;
    $[2] = task;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== referral) {
    t3 = <AutonomyReferralCards referral={referral} />;
    $[4] = referral;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}